import {ReactNode} from 'react';
import {BrowserRouter, Routes, Route, Navigate} from 'react-router-dom';
import routes from './components/routes/routes';
import AddPlacesScreen from './components/screens/AddPlacesScreen';
import AddEventsScreen from './components/screens/AddEventsScreen';
import NotFound from './components/screens/notFOund/notFOund';
import EventDetailsScreen from './components/screens/eventsScreens/EventDetailsScreen';
import EventListScreen from './components/screens/eventsScreens/EventListScreen';
import CreateEventScreen from './components/screens/eventsScreens/CreateEventScreen';
import EditEvent from './components/screens/eventsScreens/EditEventScreen';
import DeleteEvent from './components/screens/eventsScreens/DeleteEventScreen';
import {SignInScreen} from './components/screens/SignInScreen';
import {AuthGuard} from './chore/guards/components/AuthGuard';
import {OrganizerSelectionScreen} from './components/screens/OrganizerSelectionScreen';
import {OrganizerGuard} from './chore/guards/components/OrganizerGuard';
import {OrganizerLayout} from './components/layouts/OrganizerLayout';
import {AdminLayout} from './components/layouts/AdminLayout';
import {FullScreenLoader} from './components/loader/FullscreenLoader';
import {useUiStore} from './common/stores/useUiStore';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {TagSelectionScreen} from './components/screens/TagSelectionScreen';
import {PlacesListScreen} from './components/screens/placesScreens/PlacesListScreen';
import {ApprovePlacesScreen} from './components/screens/placesScreens/ApprovePlacesScreen';
import {ApproveBusinessScreen} from './components/screens/placesScreens/ApproveBusinessScreen';
import {ActivitiesListScreen} from './components/screens/activitiesScreens/ActivitiesListScreen';
import {UsersScreen} from './components/screens/usersScreens/UsersScreen';
import {PlaceFormScreen} from './components/screens/placesScreens/PlaceFormScreen';
import {CitiesScreen} from './components/screens/citiesScreens/CitiesScreen';
import {TagsScreen} from './components/screens/tagsScreens/TagsScreen';
import {TokensScreen} from './components/screens/tokensScreens/TokensScreen';
import {ChallengesScreen} from './components/screens/challengesScreens/ChallengesScreen';
import {SupportScreen} from './components/screens/supportScreens/SupportScreen';

export interface AppRoute {
  path: string;
  element: ReactNode;
  children?: AppRoute[];
}

function renderRoutes(routes: AppRoute[]) {
  return routes.map(route => (
    <Route key={route.path} path={route.path} element={route.element}>
      {route.children && renderRoutes(route.children)}
    </Route>
  ));
}

// Created once: a client made inside App was replaced whenever App re-rendered
// (each time the full screen loader toggled), dropping every cached query.
const queryClient = new QueryClient({
  // One retry: with the default three a failed screen kept "Loading..." for
  // about ten seconds before showing its error.
  defaultOptions: {queries: {retry: 1}},
});

export default function App() {
  const isLoading = useUiStore(state => state.isLoading);

  const authenticationRestrictedElements = [
    {
      path: routes.organizer,
      element: <OrganizerSelectionScreen />,
    },
    {
      path: routes.approvePlaces,
      element: <ApprovePlacesScreen />,
    },
    {
      path: routes.approveBusiness,
      element: <ApproveBusinessScreen />,
    },
    {
      path: routes.activities,
      element: <ActivitiesListScreen />,
    },
    {
      path: routes.users,
      element: <UsersScreen />,
    },
    {
      path: routes.support,
      element: <SupportScreen />,
    },
    {
      path: routes.cities,
      element: <CitiesScreen />,
    },
    {
      path: routes.tags,
      element: <TagsScreen />,
    },
    {
      path: routes.tokens,
      element: <TokensScreen />,
    },
    {
      path: routes.challenges,
      element: <ChallengesScreen />,
    },
    {
      path: routes.createPlace,
      element: <PlaceFormScreen />,
    },
    {
      path: routes.editPlace,
      element: <PlaceFormScreen />,
    },
  ];

  const restrictedElements = [
    {
      path: routes.getAllPlaces,
      element: <PlacesListScreen />,
    },
    {
      path: routes.getAllEvents,
      element: <EventListScreen />,
    },
    {
      path: routes.places,
      element: <AddPlacesScreen />,
    },
    {
      path: routes.events,
      element: <AddEventsScreen />,
    },
    {
      path: routes.getEvent,
      element: <EventDetailsScreen />,
    },
    {
      path: routes.createEvent,
      element: <CreateEventScreen />,
    },
    {
      path: routes.editEvent,
      element: <EditEvent />,
    },
    {
      path: routes.deleteEvent,
      element: <DeleteEvent />,
    },
    {
      path: routes.tagSelection,
      element: <TagSelectionScreen />,
    },
  ];

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        {isLoading && <FullScreenLoader />}

        <Routes>
          <Route
            path={'/'}
            element={
              <AuthGuard>
                <OrganizerGuard>
                  <OrganizerLayout />
                </OrganizerGuard>
              </AuthGuard>
            }>
            <Route
              index
              element={<Navigate to={routes.getAllEvents} replace />}
            />
            {renderRoutes(restrictedElements)}
          </Route>
          <Route
            path={'/'}
            element={
              <AuthGuard>
                <AdminLayout />
              </AuthGuard>
            }>
            {renderRoutes(authenticationRestrictedElements)}
          </Route>

          {/* <Route path={routes.organizer} element={<OrganizerSelectionScreen />} /> */}
          <Route path={routes.signIn} element={<SignInScreen />} />
          {/* <Route path={routes.places} element={<AddPlacesScreen />} /> */}
          {/* <Route path={routes.events} element={<AddEventsScreen />} /> */}
          <Route path={routes.notFound} element={<NotFound />} />

          {/* <Route path={routes.getAllEvents} element={<GetAllEventsScreen />} /> */}
          {/* <Route path={routes.getEvent} element={<GetEventScreen />} /> */}
          {/* <Route path={routes.createEvent} element={<CreateEvent />} /> */}
          {/* <Route path={routes.editEvent} element={<EditEvent />} /> */}
          {/* <Route path={routes.deleteEvent} element={<DeleteEvent />} /> */}
        </Routes>
        {/* </main> */}
      </QueryClientProvider>
    </BrowserRouter>
  );
}
